import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import "./Runner.css";
import { Button } from "./Button";
import { Alert, EmptyState } from "./Card";
import { Field, Input } from "./Field";
import { Chip } from "./Status";
import { agoText } from "../chat/api";
import type { RunnerPairRequest, RunnerRow } from "../lib/auth";
import { tx } from "../i18n";

/* ══ 설정 › 러너의 부품 — 채택 시안 A안(Figma 15-3 · 공통 16-161) ══
   시안은 행 끝에 「…」 메뉴를 그렸지만 줄마다 할 일이 폐기 하나라 단추 하나로 둔다 — 표 한 줄 안
   단추는 하나까지, 둘 이상일 때 「…」 다(AGENTS.md 「디자인」). 거절 문장은 서버 그대로 보인다. */

/** 이 설치 안의 붙음 상태. 「안 붙어 봄」은 붙은 적이 없거나(웹) 이 기동 뒤로 모르는 것(env)이다. */
export function runnerState(r: RunnerRow): "live" | "down" | "never" {
  if (r.connections > 0) return "live";
  return r.lastSeenAt ? "down" : "never";
}

/** 끊긴 지 얼마나 — 「14분째」 · 「3시간째」 · 「2일째」. 기준 시각은 서버가 준다(`downSince`). */
export function downForText(since: string, now = Date.now()): string {
  const min = Math.max(0, Math.floor((now - Date.parse(since)) / 60_000));
  if (min < 60) return tx("runners.downFor.min", { n: min });
  const h = Math.floor(min / 60);
  return h < 48 ? tx("runners.downFor.hour", { n: h }) : tx("runners.downFor.day", { n: Math.floor(h / 24) });
}

/** 끊김 칩 — 빨강 **과** 빈 원 · 「끊김 · N분째」 글자. 서버 화면 · 러너 표가 같이 쓴다. */
export function DownChip({ since, now }: { since: string; now?: number }) {
  return (
    <Chip tone="error">
      <span aria-hidden="true">○</span> {tx("runners.state.downFor", { for: downForText(since, now) })}
    </Chip>
  );
}

/** 붙은 방식 — hello 가 밝힌 값. 안 붙었거나 옛 나루면 「모름」(빈 칸이 아니다). */
export function ModeText({ mode }: { mode: string | null | undefined }) {
  if (mode === "host" || mode === "docker")
    return (
      <span className="rmode" title={tx("runners.mode.tip")}>
        {tx(`runners.mode.${mode}`)}
      </span>
    );
  return <span className="muted">{mode ? mode : tx("runners.unknown")}</span>;
}

function StateMark({ r, now }: { r: RunnerRow; now: number }) {
  const s = runnerState(r);
  if (s === "down" && r.downSince) return <DownChip since={r.downSince} now={now} />;
  if (s === "live") {
    return (
      <Chip tone="success">
        <span aria-hidden="true">●</span> {r.connections > 1 ? tx("runners.state.liveMany", { n: r.connections }) : tx("runners.state.live")}
      </Chip>
    );
  }
  return (
    <Chip>
      <span aria-hidden="true">{s === "down" ? "○" : "◌"}</span> {tx(s === "down" ? "runners.state.down" : "runners.state.never")}
    </Chip>
  );
}

/** 사람이 읽는 날짜(그 기기 시간대) — 출처 칸에 「언제 만들었나」만 짧게 적는다. */
function day(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE");
}

export function RunnerTable({
  rows,
  now = Date.now(),
  busyName,
  error,
  armedName: armedInit = null,
  onRevoke,
}: {
  rows: RunnerRow[];
  now?: number;
  busyName?: string | null;
  error?: string | null;
  /** 첫 번째 누름을 받은 줄 — 패턴 페이지가 그 상태를 세우려고 받는다. */
  armedName?: string | null;
  onRevoke?: (name: string) => void;
}) {
  const [armed, setArmed] = useState<string | null>(armedInit);
  if (rows.length === 0) {
    return <EmptyState icon="inbox" title={tx("runners.empty.title")} desc={tx("runners.empty.desc")} />;
  }
  return (
    <>
      {error ? <Alert tone="error">{error}</Alert> : null}
      {/* 폰에서도 카드로 접지 않는다 — 틀 안에서 가로로 구른다(공통 규칙). */}
      <div className="b-table-wrap">
        <table className="b-table rtable" aria-label={tx("runners.table")}>
          <thead>
            <tr>
              <th>{tx("runners.col.state")}</th>
              <th>{tx("runners.col.name")}</th>
              <th>{tx("runners.col.mode")}</th>
              <th>{tx("runners.col.tags")}</th>
              <th>{tx("runners.col.vendors")}</th>
              <th>{tx("runners.col.version")}</th>
              <th>{tx("runners.col.seen")}</th>
              <th>{tx("runners.col.source")}</th>
              <th>{tx("runners.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const p = r.presence;
              const key = `${r.source}:${r.name}`;
              return (
                <tr key={key} className={runnerState(r) === "down" ? "rtable__row--down" : undefined}>
                  <td>
                    <StateMark r={r} now={now} />
                  </td>
                  <td className="is-primary">{r.name}</td>
                  <td>
                    <ModeText mode={p?.mode} />
                  </td>
                  {/* 붙어 있지 않으면 hello 를 모른다 — 「모름」이지 빈 값이 아니다. */}
                  <td>{p ? (p.tags === null ? tx("runners.unknown") : p.tags.length ? p.tags.join(", ") : tx("runners.noTags")) : tx("runners.unknown")}</td>
                  <td>{p ? (p.vendors.length ? p.vendors.join(", ") : tx("runners.noTags")) : tx("runners.unknown")}</td>
                  <td className="tnum">{p ? p.version : tx("runners.unknown")}</td>
                  <td>{r.lastSeenAt ? agoText(r.lastSeenAt, now) : tx("runners.unknown")}</td>
                  <td>
                    {r.source === "env"
                      ? tx("runners.source.env")
                      : tx("runners.source.web", { by: r.createdBy ?? tx("runners.unknown"), date: r.createdAt ? day(r.createdAt) : tx("runners.unknown") })}
                  </td>
                  <td>
                    {r.source === "web" ? (
                      <Button
                        size="sm"
                        variant={armed === r.name ? "danger" : "ghost"}
                        loading={busyName === r.name}
                        onClick={() => {
                          // 두 번 누른다 — 폐기는 되돌릴 수 없고, 그 서버의 나루가 바로 끊긴다.
                          if (armed === r.name) {
                            setArmed(null);
                            onRevoke?.(r.name);
                          } else setArmed(r.name);
                        }}
                        onBlur={() => setArmed((a) => (a === r.name ? null : a))}
                      >
                        {tx(armed === r.name ? "runners.revokeConfirm" : "runners.revoke")}
                      </Button>
                    ) : (
                      <span className="muted" title={tx("runners.envNote")}>
                        —
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function NewRunnerForm({
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  busy?: boolean;
  error?: string | null;
  onSubmit?: (name: string) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit?.(name.trim());
  }
  return (
    <section className="b-card rnew">
      <h3 className="b-card-title">{tx("runners.new.title")}</h3>
      {error ? <Alert tone="error">{error}</Alert> : null}
      <form className="rnew__form" onSubmit={submit}>
        <Field label={tx("runners.new.name")} hint={tx("runners.new.hint")}>
          {(id) => <Input id={id} autoComplete="off" spellCheck={false} placeholder="s114" value={name} onChange={(e) => setName(e.target.value)} required />}
        </Field>
        <div className="rnew__actions">
          <Button type="button" onClick={onCancel}>
            {tx("common.action.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {tx("runners.new.submit")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** 발급 직후 한 번만 서는 카드. 원문은 이 카드가 닫히면 화면 어디에도 없다(서버도 해시만 든다). */
export function RunnerTokenCard({ name, token, onClose }: { name: string; token: string; onClose?: () => void }) {
  const [copied, setCopied] = useState<"ok" | "fail" | null>(null);
  useEffect(() => setCopied(null), [token]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(token);
      setCopied("ok");
    } catch {
      // http 로 연 화면은 클립보드 문이 없다 — 조용히 넘기지 않고 직접 고르라고 말한다.
      setCopied("fail");
    }
  }
  const cmds = ["cd /home/irolab/services/irolab-svc-hosts", "make append REPO=b3x-in S=naru", "make up S=naru"];
  return (
    <section className="b-card rtoken" aria-live="polite">
      <h3 className="b-card-title">{tx("runners.token.title", { name })}</h3>
      <p className="muted">{tx("runners.token.lead")}</p>
      <div className="rtoken__row">
        <code className="rtoken__value">{token}</code>
        <Button size="md" onClick={copy}>
          {tx("runners.token.copy")}
        </Button>
      </div>
      {copied === "ok" ? <Alert tone="success">{tx("runners.token.copied")}</Alert> : null}
      {copied === "fail" ? <Alert tone="warning">{tx("runners.token.copyFailed")}</Alert> : null}
      <p className="muted">{tx("runners.token.where", { name })}</p>
      <pre className="rtoken__cmds">{cmds.join("\n")}</pre>
      <div className="rnew__actions">
        <Button variant="primary" onClick={onClose}>
          {tx("runners.token.done")}
        </Button>
      </div>
    </section>
  );
}

/* ══ 페어링(코드 승인) — 시안 Figma 19-2 ══
   서버가 띄운 코드를 관리자가 허용한다. 토큰은 이 화면에 **오지 않는다** — 서버가 받아 갈 때 생긴다
   (`crates/api/src/runner_pair.rs`). 이름 · 호스트는 서버가 댄 값이라 「서버가 댄」이라고 적는다. */

/** 툴바의 코드 칸. 대소문자 · 대시는 서버가 가리지 않는다 — 여기서 고치지 않고 그대로 보낸다. */
export function PairCodeForm({ onOpen }: { onOpen?: (code: string) => void }) {
  const [code, setCode] = useState("");
  return (
    <form
      className="rpair__form"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) onOpen?.(code.trim());
      }}
    >
      <Input
        aria-label={tx("runners.pair.codeLabel")}
        size="md"
        className="rpair__code"
        autoComplete="off"
        spellCheck={false}
        placeholder="XXXX-XXXX"
        value={code}
        onChange={(e) => setCode(e.target.value)}
      />
      <Button type="submit" variant="primary">
        {tx("runners.pair.open")}
      </Button>
    </form>
  );
}

export function leftText(expiresAt: string, now: number): string {
  const min = Math.floor((Date.parse(expiresAt) - now) / 60_000);
  return min < 1 ? tx("runners.pair.leftSoon") : tx("runners.pair.leftMin", { n: min });
}

export function PairRequestCard({
  req,
  now = Date.now(),
  busy,
  error,
  takenWeb: takenInit = null,
  onApprove,
  onDeny,
}: {
  req: RunnerPairRequest;
  now?: number;
  busy?: "approve" | "deny" | null;
  /** 서버가 거절한 문장(그대로 보인다). */
  error?: string | null;
  /** 이 이름이 웹 러너와 겹친다고 서버가 말했다 — 「바꿔서 허용」을 세운다. */
  takenWeb?: string | null;
  onApprove?: (name: string, replace: boolean) => void;
  onDeny?: () => void;
}) {
  const [name, setName] = useState(req.name);
  const trimmed = name.trim();
  // 서버가 댄 이름이 웹 러너와 겹치면 처음부터 안다(`nameTaken`). 다른 이름은 허용을 눌러 봐야 안다.
  const takenWeb = takenInit === trimmed || (req.nameTaken === "web" && trimmed === req.name);
  return (
    <section className="b-card rpair" aria-live="polite">
      <h3 className="b-card-title">{tx("runners.pair.title", { code: req.code })}</h3>
      <p className="muted">{tx("runners.pair.lead")}</p>
      <dl className="rpair__facts">
        <dt>{tx("runners.pair.askedName")}</dt>
        <dd className="rpair__mono">{req.name}</dd>
        <dt>{tx("runners.pair.host")}</dt>
        <dd className="rpair__mono">{req.host ?? tx("runners.unknown")}</dd>
        <dt>{tx("runners.pair.when")}</dt>
        <dd>{tx("runners.pair.whenText", { ago: agoText(req.createdAt, now), left: leftText(req.expiresAt, now) })}</dd>
      </dl>
      <form
        className="rnew__form"
        onSubmit={(e) => {
          e.preventDefault();
          onApprove?.(trimmed, takenWeb);
        }}
      >
        <Field label={tx("runners.pair.name")} hint={tx("runners.pair.nameHint")}>
          {(id) => <Input id={id} autoComplete="off" spellCheck={false} value={name} onChange={(e) => setName(e.target.value)} required />}
        </Field>
        {takenWeb ? <Alert tone="warning">{tx("runners.pair.takenWeb", { name: trimmed })}</Alert> : null}
        {error ? <Alert tone="error">{error}</Alert> : null}
        <div className="rnew__actions">
          <Button type="button" loading={busy === "deny"} disabled={!!busy} onClick={onDeny}>
            {tx("runners.pair.deny")}
          </Button>
          <Button type="submit" variant={takenWeb ? "danger" : "primary"} loading={busy === "approve"} disabled={!!busy}>
            {tx(takenWeb ? "runners.pair.replace" : "runners.pair.approve")}
          </Button>
        </div>
      </form>
    </section>
  );
}

/** 연결 요청의 끝 — 허용됨 · 거절함 · 코드 없음(틀림 · 만료 · 이미 받아 감). */
export function PairEndCard({ kind, name, onClose }: { kind: "approved" | "denied" | "missing"; name?: string; onClose?: () => void }) {
  return (
    <section className={`b-card rpair${kind === "approved" ? " is-done" : ""}`} aria-live="polite">
      <h3 className="b-card-title">{tx(`runners.pair.${kind}.title`, { name: name ?? "" })}</h3>
      {kind === "approved" ? <Alert tone="success">{tx("runners.pair.approved.note")}</Alert> : null}
      <p className="muted">{tx(`runners.pair.${kind}.desc`)}</p>
      <div className="rnew__actions">
        <Button variant={kind === "approved" ? "primary" : "secondary"} onClick={onClose}>
          {tx("runners.pair.close")}
        </Button>
      </div>
    </section>
  );
}
